import type { Sku } from "../types";

/**
 * Gemini on Vertex's global endpoint. Where a model has long-context rows, a prompt over 200K tokens
 * prices the whole call at them; priority costs 1.8 times on-demand, flex half.
 */
export const vertexSkus: readonly Sku[] = [
  {
    id: "vertex:gemini-3.1-pro-preview:global:ON_DEMAND",
    billedBy: "vertex",
    model: "gemini-3.1-pro-preview",
    endpoint: "global",
    tier: "ON_DEMAND",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 12,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "uncachedInput.over-200k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 4,
        promptTokens: { over: 200_000 },
      },
      {
        id: "implicitCacheRead.over-200k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.4,
        promptTokens: { over: 200_000 },
      },
      {
        id: "output.over-200k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 18,
        promptTokens: { over: 200_000 },
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
  {
    id: "vertex:gemini-3.1-pro-preview:global:ON_DEMAND_PRIORITY",
    billedBy: "vertex",
    model: "gemini-3.1-pro-preview",
    endpoint: "global",
    tier: "ON_DEMAND_PRIORITY",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 3.6,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.36,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 21.6,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "uncachedInput.over-200k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 7.2,
        promptTokens: { over: 200_000 },
      },
      {
        id: "implicitCacheRead.over-200k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.72,
        promptTokens: { over: 200_000 },
      },
      {
        id: "output.over-200k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 32.4,
        promptTokens: { over: 200_000 },
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
  {
    id: "vertex:gemini-3.1-pro-preview:global:ON_DEMAND_FLEX",
    billedBy: "vertex",
    model: "gemini-3.1-pro-preview",
    endpoint: "global",
    tier: "ON_DEMAND_FLEX",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 1,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.1,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "output",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 6,
        promptTokens: { upTo: 200_000 },
      },
      {
        id: "uncachedInput.over-200k",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2,
        promptTokens: { over: 200_000 },
      },
      {
        id: "implicitCacheRead.over-200k",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.2,
        promptTokens: { over: 200_000 },
      },
      {
        id: "output.over-200k",
        meter: "output",
        unit: "1M tokens",
        currency: "USD",
        amount: 9,
        promptTokens: { over: 200_000 },
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
  {
    id: "vertex:gemini-3.8-flash:global:ON_DEMAND",
    billedBy: "vertex",
    model: "gemini-3.8-flash",
    endpoint: "global",
    tier: "ON_DEMAND",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.75,
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.075,
      },
      { id: "output", meter: "output", unit: "1M tokens", currency: "USD", amount: 3.75 },
      {
        id: "uncachedAudioInput",
        meter: "uncachedAudioInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.5,
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
  {
    id: "vertex:gemini-3.8-flash:global:ON_DEMAND_PRIORITY",
    billedBy: "vertex",
    model: "gemini-3.8-flash",
    endpoint: "global",
    tier: "ON_DEMAND_PRIORITY",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 1.35,
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.135,
      },
      { id: "output", meter: "output", unit: "1M tokens", currency: "USD", amount: 6.75 },
      {
        id: "uncachedAudioInput",
        meter: "uncachedAudioInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 2.7,
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
  {
    id: "vertex:gemini-3.8-flash:global:ON_DEMAND_FLEX",
    billedBy: "vertex",
    model: "gemini-3.8-flash",
    endpoint: "global",
    tier: "ON_DEMAND_FLEX",
    promptTokensCount: "allInput",
    maxOutputTokens: 65_536,
    prices: [
      {
        id: "uncachedInput",
        meter: "uncachedInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.375,
      },
      {
        id: "implicitCacheRead",
        meter: "implicitCacheRead",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.0375,
      },
      { id: "output", meter: "output", unit: "1M tokens", currency: "USD", amount: 1.875 },
      {
        id: "uncachedAudioInput",
        meter: "uncachedAudioInput",
        unit: "1M tokens",
        currency: "USD",
        amount: 0.75,
      },
      {
        id: "tool:google_search",
        meter: "tool:google_search",
        unit: "1k uses",
        currency: "USD",
        amount: 14,
      },
    ],
  },
];
